import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import moment from 'moment';
import { Grid } from 'web-ui-components/lib/layout/grids';
import { LoadingLocalTakeover } from 'web-ui-components/lib/organisms/takeovers';
import { BackButton } from 'web-ui-components/lib/molecules/navigation';
import { PageHeading, SectionHeading, SubHeading } from 'web-ui-components/lib/typography/headings';
import { StandardBody } from 'web-ui-components/lib/typography/body';
import { Button } from 'web-ui-components/lib/atoms/buttons';
import { ButtonGroup } from 'web-ui-components/lib/molecules/buttons';
import { ContrastingFormPanel } from 'web-ui-components/lib/molecules/panels';
import { IconText } from 'web-ui-components/lib/molecules/icon-text';
import { ShellInnerSpacer } from 'web-ui-components/lib/layout/shells';
import { isString, isEqual, get, head } from 'lodash';
import HardTokenAuthorisation from 'hard-token-challenge-lib';
import { EffectiveWarning } from 'payments-lib';

import SinglePaymentDetails from '../PaymentDetails/SinglePaymentDetails/SinglePaymentDetails';
import RepeatingPaymentDetails from '../PaymentDetails/RepeatingPaymentDetails/RepeatingPaymentDetails';
import BatchPaymentDetails from '../PaymentDetails/batch/BatchPaymentDetails';
import AuthoriseChapsPaymentDetails from '../PaymentDetails/chaps/ChapsPaymentDetails';
import AuthoriseBatchChapsPaymentDetails from '../PaymentDetails/chaps/ChapsBatchPaymentDetails';

import AuthoriseBacsCreditPaymentDetails from './AuthoriseBacsCreditPaymentDetails';
import ViewBacsPaymentDetailsForOtherUsers from './ViewBacsPaymentDetailsForOtherUsers';
import AuthoriseBacsDebitPaymentDetails from './AuthoriseBacsDebitPaymentDetails';

import withContent from '../../containers/withContent';
import withScrollTop from '../../containers/withScrollTop';
import authoriseContent, { contentPropTypes } from './Authorise.content';
import DeadlineMissed from '../DeadlineMissed/DeadlineMissed';
import AuthorisedModel from '../AuthorisedModal/AuthorisedModal';
import {
	isBatch,
	isRepeatingPaymentToAuthorise,
	getBatchName,
	getBacsName,
	getToAuthorise,
	isToAuthoriseBacs,
	isChaps,
	getToViewForOthers,
	getActiveAwaitingAuthTab,
	isFileUploadDeadlineMessage,
} from '../../states/payments/builders';
import { verificationPropTypes } from '../../states/payments/propTypes';
import {
	getClientContext,
	getBankId,
	getToken,
	getIsSme,
	getHasGroupApprovalsEnabled,
} from '../../states/session/selectors';
import { getPAASApiBaseUrl, getApiBaseUrl, getBankOrBrandId } from '../../states/envConfig/selectors';
import { clientContextPropTypes } from '../../states/session/propTypes';
import {
	BACS_CREDIT_CODE,
	TYPE_IFT,
	AUTHORISE_FINAL_APPROVAL_ERROR_CODE,
	AUTHORISE_BUSINESS_CUTOFF_ERROR_CODE,
	fxRatesType,
} from '../../config';
import {
	goBackPage,
	acceptNewDates,
	fetchMyPaymentsToAuthorise,
	cancelPayment,
	reviewPayment,
	closeAlert,
} from '../../states/page/actions';
import { Cancel } from '../Decline/Decline';
import AuthoriseIFTPayment from './AuthoriseIFTPayment';
import withFxRates from '../../containers/withFxRates';
import {
	getRatesRequestForAuthorisation,
	getNewSubmissionAndSettlementDates,
	isPaymentAuthoriseAllowed,
} from '../../states/rates/selectors';
import FinalApprovalModal from '../FinalApprovalModal/FinalApprovalModal';
import BusinessCutOffModal from '../BusinessCutOffModal/BusinessCutOffModal';
import RatesErrorModal from '../RatesErrorModal/RatesErrorModal';
import SoftTokenStatusTable from './SoftTokenStatusTable';
import { isFileUploadPayment } from '../../utils/paymentsUtils';
import {
	hasFileUploadError,
	hasErrorDeadline,
	fileUploadReviewed,
	fileUploadSettlementDate,
	fileUploadSubmissionDate,
} from '../../states/page/selectors';
import NewPaymentDateModal from '../NewPaymentDateModal/NewPaymentDateModal';
import withFxRatesCounter from '../../containers/withFxRatesCounter';
import { REVIEWED, PENDING, SOFT, CONST_YOU } from '../../constants/Constants';
import ViewBatchPaymentDetailsForOtherUsers from './ViewBatchPaymentDetailsForOtherUsers';
import StandoutTileMessage from '../StandoutTileMessage/StandoutTitle';
import { getUser } from '../../states/user/selectors';

const mapState = (state) => ({
	user: getUser(state),
	isBatch: isBatch(state),
	isBacs: isToAuthoriseBacs(state),
	isChaps: isChaps(state),
	isSme: getIsSme(state),
	hasGroupApprovalsEnabled: getHasGroupApprovalsEnabled(state),
	isRepeatingPayment: isRepeatingPaymentToAuthorise(state),
	batchName: getBatchName(state),
	bacsName: getBacsName(state),
	brandId: getBankOrBrandId(state),
	toAuthorise: getToAuthorise(state),
	clientContext: getClientContext(state),
	bankId: getBankId(state),
	accessToken: getToken(state),
	apiBaseUrlPAAS: getPAASApiBaseUrl(state),
	apiBaseUrl: getApiBaseUrl(state),
	ratesRequestForAuthorisation: getRatesRequestForAuthorisation(state),
	newPaymentDates: getNewSubmissionAndSettlementDates(state),
	fileUploadPaymentByOthers: getToViewForOthers(state),
	hasFileUploadError: hasFileUploadError(state),
	fileErrorDeadline: hasErrorDeadline(state),
	fileUploadReviewed: fileUploadReviewed(state),
	fileUploadSettlementDate: fileUploadSettlementDate(state),
	fileUploadSubmissionDate: fileUploadSubmissionDate(state),
	getActiveAwaitingAuthTab: getActiveAwaitingAuthTab(state),
	isFileUploadDeadlineMessage: isFileUploadDeadlineMessage(state),
	isPaymentAuthoriseAllowed: isPaymentAuthoriseAllowed(state),
});

const mapDispatch = (dispatch) => ({
	goBack: () => dispatch(goBackPage()),
	acceptNewDates: (verificationId, settlementDate, ratesRequestForAuthorisation) =>
		dispatch(acceptNewDates(verificationId, settlementDate, ratesRequestForAuthorisation)),
	fetchMyPaymentsToAuthorise: () => dispatch(fetchMyPaymentsToAuthorise()),
	handleCancel: (paymentId) => dispatch(cancelPayment(paymentId)),
	handleReview: (paymentId) => dispatch(reviewPayment(paymentId)),
	closeFileReviewedAlert: (value) => dispatch(closeAlert(value)),
});

const getPaymentDateModalContentKey = (isChaps, isPaymentAuthoriseAllowed) => {
	if (isChaps) {
		return 'chapsPaymentContent';
	}
	return isPaymentAuthoriseAllowed ? 'currentDatedPaymentContent' : 'futureDatedPaymentContent';
};

const isIFTPreBooked = (authObj) => get(authObj, 'ift.indicative_fx.type') === fxRatesType.PRE_BOOKED_RATE;

class Authorise extends Component {
	constructor(props) {
		super(props);
		this.state = {
			deadlineMissed: false,
			authorisedModal: false,
			cancelModal: false,
			finalApprovalModal: false,
			businessCutOffModal: false,
			showNewPaymentDateModal: false,
			showApproverWarning: true,
			showEWAdditionalGuidanceScreen: false,
		};

		this.handleError = this.handleError.bind(this);
		this.handleCloseDeadlineMissedModal = this.handleCloseDeadlineMissedModal.bind(this);
		this.handleAcceptNewDates = this.handleAcceptNewDates.bind(this);
		this.handleAuthorisedSuccess = this.handleAuthorisedSuccess.bind(this);
		this.handleCloseCancelModel = this.handleCloseCancelModel.bind(this);
		this.handleShowCancelPaymentModal = this.handleShowCancelPaymentModal.bind(this);
		this.getTitle = this.getTitle.bind(this);
		this.getSubTitle = this.getSubTitle.bind(this);
		this.renderPaymentDetails = this.renderPaymentDetails.bind(this);
		this.handleFinalApprovalModalContinue = this.handleFinalApprovalModalContinue.bind(this);
		this.handleAcceptAndAuthorisePayment = this.handleAcceptAndAuthorisePayment.bind(this);
		this.handleEffectiveWarningActions = this.handleEffectiveWarningActions.bind(this);
	}

	componentDidUpdate(prevProps, prevState) {
		const {
			toAuthorise,
			hasFileUploadError,
			fileErrorDeadline,
			fileUploadReviewed,
			goBack,
			fileUploadSettlementDate,
			fileUploadSubmissionDate,
			newPaymentDates: { isPaymentDatesUpdated },
		} = this.props;
		const {
			newPaymentDates: { isPaymentDatesUpdated: prevPropsIsPaymentDateUpdated },
		} = prevProps;
		const fileUploadCheck = hasFileUploadError && !isEqual(hasFileUploadError, prevProps.hasFileUploadError);
		if (prevState.deadlineMissed === true && !isEqual(toAuthorise, prevProps.toAuthorise)) {
			// eslint-disable-next-line react/no-did-update-set-state
			this.setState({ deadlineMissed: false });
		}
		if (fileUploadCheck && fileErrorDeadline) {
			// eslint-disable-next-line react/no-did-update-set-state
			this.setState({
				deadlineMissed: true,
				deadlineMissedSettlementDate: moment(fileUploadSettlementDate),
				deadlineMissedSubmissionDate: moment(fileUploadSubmissionDate),
			});
		}
		if (
			(fileUploadReviewed && fileUploadReviewed !== prevProps.fileUploadReviewed) ||
			(fileUploadCheck && !fileErrorDeadline)
		) {
			goBack();
		}
		if (!isEqual(isPaymentDatesUpdated, prevPropsIsPaymentDateUpdated)) {
			// eslint-disable-next-line react/no-did-update-set-state
			this.setState({ showNewPaymentDateModal: isPaymentDatesUpdated });
		}
	}

	getTitleForFileUpload(status, paymentName) {
		const {
			content: { reviewAndConfirm, aboutThisPayment },
		} = this.props;
		return status === PENDING ? `${reviewAndConfirm} ${paymentName}` : aboutThisPayment;
	}

	getTitle() {
		const {
			isBatch,
			isBacs,
			isChaps,
			batchName,
			bacsName,
			content: { batchTitle, bacsTitle, title, chapsTitle, aboutThisPayment },
			toAuthorise: { bacs, status, verification_type },
			getActiveAwaitingAuthTab,
		} = this.props;
		if (getActiveAwaitingAuthTab !== CONST_YOU) {
			return aboutThisPayment;
		}
		if (isBatch) {
			return verification_type === SOFT && get(head(), 'file_upload')
				? this.getTitleForFileUpload(status, batchName)
				: `${batchTitle} ${batchName}`;
		}

		if (isBacs) {
			return verification_type === SOFT && bacs.file_upload
				? this.getTitleForFileUpload(status, bacsName)
				: `${bacsTitle} ${bacsName}`;
		}
		if (isChaps) {
			return chapsTitle;
		}
		return title;
	}
	getSubTitle() {
		const {
			isBacs,
			isChaps,
			content: {
				bacsAuthoriseMsg,
				bacsPaymentDeadlineTitle,
				iftAuthoriseMsg,
				iftNotFinalApprovalMsg,
				iftPreBookedRateTitle,
				refreshLiveRateMsg,
				iftPreBookedRateMsg,
				iftNotFinalApprovalTitle,
				chapsAuthoriseMsg,
				iftSameCurrencyTitle,
			},
			toAuthorise,
			showLiveRate,
		} = this.props;
		if (isBacs) {
			return (
				<div>
					<SectionHeading>{bacsPaymentDeadlineTitle}</SectionHeading>
					<StandardBody>
						<p>{bacsAuthoriseMsg}</p>
					</StandardBody>
				</div>
			);
		}
		if (isChaps) {
			return (
				<StandardBody>
					<p>{chapsAuthoriseMsg}</p>
				</StandardBody>
			);
		}
		if (toAuthorise.type === TYPE_IFT) {
			if (isIFTPreBooked(toAuthorise)) {
				return (
					<div>
						<SubHeading>{iftPreBookedRateTitle}</SubHeading>
						<StandardBody>
							<p>{iftPreBookedRateMsg}</p>
						</StandardBody>
					</div>
				);
			}

			if (!toAuthorise.final_approval) {
				if (!showLiveRate) {
					return <SubHeading>{iftNotFinalApprovalTitle}</SubHeading>;
				}
				return (
					<div>
						<SubHeading>{iftNotFinalApprovalTitle}</SubHeading>
						<StandardBody>
							<p>{iftNotFinalApprovalMsg}</p>
						</StandardBody>
					</div>
				);
			}
			if (!showLiveRate) {
				return <SubHeading>{iftSameCurrencyTitle}</SubHeading>;
			}
			return (
				<div>
					<SubHeading>{iftAuthoriseMsg}</SubHeading>
					<StandardBody>
						<p>{refreshLiveRateMsg}</p>
					</StandardBody>
				</div>
			);
		}
		return null;
	}

	handleError(err) {
		const errorResponseBody = get(err, 'response');
		const errorStatus = get(err, 'status');
		if (errorStatus === 409 && errorResponseBody) {
			return this.setState({
				deadlineMissed: true,
				deadlineMissedSettlementDate: moment(errorResponseBody.new_settlement_date),
				deadlineMissedSubmissionDate: moment(errorResponseBody.new_submission_date),
			});
		}

		if (errorStatus === 412 && errorResponseBody) {
			const errorCode = get(errorResponseBody, 'code');
			if (errorCode === AUTHORISE_FINAL_APPROVAL_ERROR_CODE) {
				return this.setState({
					finalApprovalModal: true,
				});
			}

			if (errorCode === AUTHORISE_BUSINESS_CUTOFF_ERROR_CODE) {
				return this.setState({
					businessCutOffModal: true,
				});
			}
		}
		return this.props.handleRoute('WEB-ERROR');
	}

	handleCloseDeadlineMissedModal() {
		this.setState({ deadlineMissed: false });
	}

	handleAcceptAndAuthorisePayment(isCurrentDated, isChaps) {
		if (isCurrentDated || isChaps) {
			return this.setState({
				showNewPaymentDateModal: false,
			});
		}
		return this.props.goBack();
	}

	handleAcceptNewDates() {
		const {
			acceptNewDates,
			toAuthorise: { verification_type, verification_id },
			ratesRequestForAuthorisation,
		} = this.props;
		const { deadlineMissedSubmissionDate } = this.state;
		acceptNewDates(
			{ toAuthorise: { verification_type, verification_id } },
			deadlineMissedSubmissionDate,
			ratesRequestForAuthorisation
		).then(() =>
			this.setState({
				deadlineMissed: false,
				authorisedModal: true,
			})
		);
	}

	handleAuthorisedSuccess() {
		const { goBack, fetchMyPaymentsToAuthorise } = this.props;
		goBack();
		fetchMyPaymentsToAuthorise();
	}

	handleCloseCancelModel() {
		const { goBack } = this.props;
		this.setState(
			{
				cancelModal: false,
			},
			() => {
				goBack();
			}
		);
	}

	handleShowCancelPaymentModal() {
		this.props.closeFileReviewedAlert(false);
		this.setState({
			cancelModal: true,
			deadlineMissed: false,
			businessCutOffModal: false,
			showNewPaymentDateModal: false,
		});
	}

	handleFinalApprovalModalContinue() {
		const isFinalApproval = true;
		this.props.fetchFxRates(isFinalApproval);
		this.setState({
			finalApprovalModal: false,
		});
	}

	handleEffectiveWarningActions(type, details) {
		switch (type) {
			case 'CONTINUE':
				return this.setState({ showApproverWarning: false });
			case 'ON_PAYMENT_GUIDANCE':
				return this.setState({
					showEWAdditionalGuidanceScreen: details.val,
				});
			case 'CANCEL_PAYMENT':
				return this.props.goBack();
			case 'ERROR':
				return this.props.handleRoute('WEB-ERROR');
			default:
				return null;
		}
	}

	renderPaymentDetailsForAnotherTab() {
		const { fileUploadPaymentByOthers, goBack } = this.props;
		if (fileUploadPaymentByOthers && fileUploadPaymentByOthers.batch) {
			return <ViewBatchPaymentDetailsForOtherUsers payment={fileUploadPaymentByOthers} goBack={goBack} />;
		}
		return <ViewBacsPaymentDetailsForOtherUsers payment={fileUploadPaymentByOthers} goBack={goBack} />;
	}

	renderIftPaymentDetails() {
		const { toAuthorise, counter, showInitialGetRate, showRefresh, fetchFxRates } = this.props;
		const {
			ift: { creditor_currency, debtor_currency },
		} = toAuthorise;
		const isSameCurrencyPayment = creditor_currency === debtor_currency;
		return (
			<AuthoriseIFTPayment
				toAuthorise={toAuthorise}
				counter={counter}
				showExchangeRate={!!counter}
				showInitialGetRate={showInitialGetRate}
				showRefresh={showRefresh}
				onRefresh={() => fetchFxRates()}
				isIFTPreBooked={isIFTPreBooked(toAuthorise)}
				isSameCurrencyPayment={isSameCurrencyPayment}
			/>
		);
	}

	renderPaymentDetails() {
		const { isBatch, isBacs, isChaps, isRepeatingPayment, toAuthorise, newPaymentDates, fileUploadPaymentByOthers } =
			this.props;

		const isFileUploadBatchPayment = isFileUploadPayment(toAuthorise);

		if (isBatch) {
			if (isChaps)
				return (
					<AuthoriseBatchChapsPaymentDetails
						payment={toAuthorise}
						newPaymentDates={newPaymentDates}
						isFileUploadBatchPayment={isFileUploadBatchPayment}
					/>
				);
			return <BatchPaymentDetails payment={toAuthorise} isFileUploadBatchPayment={isFileUploadBatchPayment} />;
		}
		if (isBacs) {
			if (toAuthorise.bacsType === BACS_CREDIT_CODE) {
				return <AuthoriseBacsCreditPaymentDetails payment={toAuthorise} />;
			}
			return <AuthoriseBacsDebitPaymentDetails payment={toAuthorise} />;
		}
		if (isRepeatingPayment) return <RepeatingPaymentDetails payment={toAuthorise} />;
		if (isChaps) {
			return <AuthoriseChapsPaymentDetails payment={toAuthorise} newPaymentDates={newPaymentDates} />;
		}
		return <SinglePaymentDetails payment={toAuthorise} fileUploadPaymentByOthers={fileUploadPaymentByOthers} />;
	}

	renderHardOrSoftTokenAuthorisation() {
		const {
			accessToken,
			bankId,
			clientContext,
			apiBaseUrlPAAS,
			apiBaseUrl,
			toAuthorise,
			goBack,
			content,
			ratesRequestForAuthorisation,
			fetchFxRates,
			handleReview,
			counter,
			showRefresh,
			showLiveRate,
			isRatesApiInProgress,
			user,
			brandId,
			isBatch,
			isSme,
			showInitialGetRate,
		} = this.props;

		const config = {
			apiUrl: apiBaseUrl,
			PAASUrl: apiBaseUrlPAAS,
			clientContext: isString(clientContext) ? JSON.parse(clientContext) : clientContext,
			bankId,
			/** @todo in `hard-token-challenge-lib` consider renaming to `brandId` for consistency */
			brand: brandId,
			accessToken,
		};

		const { ift = null, verification_type, status, verification_id, type: paymentType } = toAuthorise;
		const isSameCurrencyPayment = ift ? ift.creditor_currency === ift.debtor_currency : false;
		if (verification_type === SOFT) {
			if (status === REVIEWED) {
				return (
					<Button id="button-back" display="secondary" width="flush" onClick={goBack}>
						{content.back}
					</Button>
				);
			}
			return (
				<div>
					<ContrastingFormPanel>
						<IconText iconType="MobileOutlineLarge">{content.twoStepAuthoriseContent}</IconText>
					</ContrastingFormPanel>
					<ButtonGroup>
						<Button id="button-back" display="secondary" width="flush" onClick={goBack}>
							{content.back}
						</Button>
						<Button id="authorise-button" display="primary" width="flush" onClick={() => handleReview(verification_id)}>
							{content.authoriseOnMobile}
						</Button>
					</ButtonGroup>
				</div>
			);
		}

		if (this.state.showApproverWarning && paymentType !== TYPE_IFT) {
			return (
				<div>
					<EffectiveWarning
						startPage="Warning"
						envConfig={{
							apiBaseUrl,
							apiBaseUrlPAAS,
							bankId,
							brandId,
						}}
						session={{
							...config,
							clientContext,
						}}
						effectiveWarning={{
							approverData: toAuthorise,
							isApprovalJourney: true,
							isBatch: isBatch,
						}}
						user={user}
						onAction={this.handleEffectiveWarningActions}
						onContinue={() => this.handleEffectiveWarningActions('CONTINUE')}
					/>
				</div>
			);
		}

		return (
			<div>
				<HardTokenAuthorisation
					verificationId={verification_id}
					mode="verification"
					config={config}
					onError={this.handleError}
					onSuccess={goBack}
					onFail={this.handleError}
					exitApp={goBack}
					ratesRequestForAuthorisation={ratesRequestForAuthorisation}
					counter={counter}
					showRefresh={showRefresh}
					onRefresh={() => fetchFxRates()}
					disableAuthorise={showRefresh || isRatesApiInProgress}
					showAuthoriseCounter={showLiveRate}
					paymentType={paymentType}
					isSme={isSme}
					isIFTPreBooked={isIFTPreBooked(toAuthorise)}
					isSameCurrencyPayment={isSameCurrencyPayment}
					showInitialGetRate={showInitialGetRate}
				/>
			</div>
		);
	}

	render() {
		const {
			toAuthorise,
			handleCancel,
			goBack,
			fileUploadPaymentByOthers,
			isRatesApiInProgress,
			ratesError,
			content,
			isChaps,
			getActiveAwaitingAuthTab,
			newPaymentDates: { newSettlementDate, newSubmissionDate, isCurrentDated = false },
			isFileUploadDeadlineMessage,
			isPaymentAuthoriseAllowed,
			hasGroupApprovalsEnabled,
		} = this.props;

		const { verification_type, status, type, verification_id } = toAuthorise;

		const {
			deadlineMissed,
			deadlineMissedSubmissionDate,
			deadlineMissedSettlementDate,
			authorisedModal,
			cancelModal,
			finalApprovalModal,
			businessCutOffModal,
			showNewPaymentDateModal,
			showEWAdditionalGuidanceScreen,
		} = this.state;
		const pendingCheck = verification_type === SOFT && status === PENDING;
		const byYouTabActive = getActiveAwaitingAuthTab === CONST_YOU;

		return (
			<div>
				{!showEWAdditionalGuidanceScreen && (
					<LoadingLocalTakeover show={isRatesApiInProgress} title="Getting the best rates...">
						<ShellInnerSpacer>
							<BackButton onClick={goBack} />
						</ShellInnerSpacer>
						<Grid.ThreeToOne rightSidebar={null}>
							<PageHeading>{this.getTitle()}</PageHeading>
							<StandoutTileMessage
								showStandoutTile={isFileUploadDeadlineMessage}
								contentKey="chapsFileUploadDeadlineMissed"
							/>
							{this.getSubTitle()}
						</Grid.ThreeToOne>
						{toAuthorise.type === TYPE_IFT ? (
							<Grid.FullWidth>
								{pendingCheck && content.twoStepAuthoriseInstruction}
								<SoftTokenStatusTable
									toAuthorise={toAuthorise}
									fileUploadPaymentByOthers={fileUploadPaymentByOthers}
									byYouTabActive={byYouTabActive}
									hasGroupApprovalsEnabled={hasGroupApprovalsEnabled}
								/>
								{byYouTabActive ? this.renderIftPaymentDetails() : this.renderPaymentDetailsForAnotherTab()}
							</Grid.FullWidth>
						) : (
							<div>
								{pendingCheck && content.twoStepAuthoriseInstruction}
								<SoftTokenStatusTable
									toAuthorise={toAuthorise}
									fileUploadPaymentByOthers={fileUploadPaymentByOthers}
									byYouTabActive={byYouTabActive}
									hasGroupApprovalsEnabled={hasGroupApprovalsEnabled}
								/>
								{!byYouTabActive
									? this.renderPaymentDetailsForAnotherTab()
									: byYouTabActive && this.renderPaymentDetails()}
							</div>
						)}

						{deadlineMissed && (
							<DeadlineMissed
								settlementDate={deadlineMissedSettlementDate}
								submissionDate={deadlineMissedSubmissionDate}
								handleCancel={this.handleCloseDeadlineMissedModal}
								handleCancelPayment={this.handleShowCancelPaymentModal}
								handleAcceptNewDates={this.handleAcceptNewDates}
								paymentType={type}
							/>
						)}
						{authorisedModal && (
							<AuthorisedModel isOpen={this.state.authorisedModal} handleClickOk={this.handleAuthorisedSuccess} />
						)}
						{cancelModal && (
							<Cancel
								handleConfirm={() => handleCancel(verification_id)}
								handleCancelPrompt={this.handleCloseCancelModel}
								handleClose={this.handleAuthorisedSuccess}
							/>
						)}
						{finalApprovalModal && (
							<FinalApprovalModal isOpen={finalApprovalModal} handleContinue={this.handleFinalApprovalModalContinue} />
						)}
						{businessCutOffModal && (
							<BusinessCutOffModal
								isOpen={businessCutOffModal}
								handleCancelPayment={this.handleShowCancelPaymentModal}
								handleAuthoriseLater={goBack}
								contentKey={isChaps ? 'chapsBusinessCutOffModalContent' : 'businessCutOffModalContent'}
							/>
						)}
						{showNewPaymentDateModal && (
							<NewPaymentDateModal
								isChaps={isChaps}
								newSettlementDate={newSettlementDate}
								newSubmissionDate={newSubmissionDate}
								handleCancelPayment={this.handleShowCancelPaymentModal}
								isPaymentAuthoriseAllowed={isPaymentAuthoriseAllowed}
								contentKey={getPaymentDateModalContentKey(isChaps, isPaymentAuthoriseAllowed)}
								handleAuthorisePayment={this.handleAcceptAndAuthorisePayment}
							/>
						)}
						{ratesError && <RatesErrorModal isOpen handleRatesModalOkClick={this.props.goBack} />}
					</LoadingLocalTakeover>
				)}
				{byYouTabActive && this.renderHardOrSoftTokenAuthorisation()}
			</div>
		);
	}
}

Authorise.propTypes = {
	isBatch: PropTypes.bool.isRequired,
	isBacs: PropTypes.bool.isRequired,
	isChaps: PropTypes.bool.isRequired,
	batchName: PropTypes.string,
	bacsName: PropTypes.string,
	content: PropTypes.shape(contentPropTypes),
	isRepeatingPayment: PropTypes.bool,
	toAuthorise: PropTypes.shape(verificationPropTypes).isRequired,
	clientContext: PropTypes.shape(clientContextPropTypes).isRequired,
	bankId: PropTypes.string.isRequired,
	isRatesApiInProgress: PropTypes.bool.isRequired,
	accessToken: PropTypes.string.isRequired,
	goBack: PropTypes.func.isRequired,
	apiBaseUrl: PropTypes.string.isRequired,
	apiBaseUrlPAAS: PropTypes.string.isRequired,
	handleRoute: PropTypes.func.isRequired,
	acceptNewDates: PropTypes.func.isRequired,
	fetchMyPaymentsToAuthorise: PropTypes.func.isRequired,
	handleCancel: PropTypes.func.isRequired,
	ratesRequestForAuthorisation: PropTypes.shape({
		indicative: PropTypes.bool,
		rates: PropTypes.arrayOf(
			PropTypes.shape({
				payment_uuid: PropTypes.string,
			})
		),
	}),
	fxChargeAmount: PropTypes.shape({
		currency: PropTypes.string,
		value: PropTypes.string,
	}).isRequired,
	ratesError: PropTypes.shape({
		message: PropTypes.string,
	}).isRequired,
	fetchFxRates: PropTypes.func.isRequired,
	fileUploadPaymentByOthers: PropTypes.shape(verificationPropTypes).isRequired,
	handleReview: PropTypes.func.isRequired,
	hasFileUploadError: PropTypes.bool,
	fileUploadReviewed: PropTypes.bool,
	closeFileReviewedAlert: PropTypes.func,
	fileErrorDeadline: PropTypes.bool,
	fileUploadSettlementDate: PropTypes.string,
	fileUploadSubmissionDate: PropTypes.string,
	newPaymentDates: PropTypes.shape({}),
	counter: PropTypes.number,
	showInitialGetRate: PropTypes.bool,
	showRefresh: PropTypes.bool,
	showLiveRate: PropTypes.bool,
	getActiveAwaitingAuthTab: PropTypes.string,
	isFileUploadDeadlineMessage: PropTypes.bool.isRequired,
	user: PropTypes.shape({}),
	brandId: PropTypes.string.isRequired,
};

export default compose(
	connect(mapState, mapDispatch),
	withContent({ content: authoriseContent }),
	withScrollTop(),
	withFxRatesCounter,
	withFxRates
)(Authorise);

export const ConnectWrapper = connect(mapState)(Authorise);
